/**
 * Date formatting utilities for debt security coupon UI.
 * All functions use `timeZone: 'UTC'` to avoid local-timezone shifts on date-only strings.
 */

function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
}

/**
 * Formats an ISO date string as "Apr 15, 2026".
 * Returns an empty string for invalid input.
 */
export function formatShortDate(iso: string): string {
  try {
    const date = new Date(iso);
    if (isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    }).format(date);
  } catch {
    return "";
  }
}

/**
 * Formats an ISO date string as an abbreviated weekday name, e.g. "Wed".
 * Returns an empty string for invalid input.
 */
export function formatDayOfWeek(iso: string): string {
  try {
    const date = new Date(iso);
    if (isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      timeZone: "UTC",
    }).format(date);
  } catch {
    return "";
  }
}

/**
 * Formats an ISO date string as "Wednesday, 27th May 2036".
 * Returns an empty string for invalid input.
 */
export function formatLongDate(iso: string): string {
  try {
    const date = new Date(iso);
    if (isNaN(date.getTime())) return "";

    const weekday = new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      timeZone: "UTC",
    }).format(date);

    const day = new Intl.DateTimeFormat("en-US", {
      day: "numeric",
      timeZone: "UTC",
    }).format(date);

    const month = new Intl.DateTimeFormat("en-US", {
      month: "long",
      timeZone: "UTC",
    }).format(date);

    const year = new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      timeZone: "UTC",
    }).format(date);

    return `${weekday}, ${ordinal(Number(day))} ${month} ${year}`;
  } catch {
    return "";
  }
}
